import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Response } from "../types";

import { UpdateInputVariableSetRequest, InputVariableSet } from "./types";

import { jsonAuthHeaders } from "@/utils/headers";
import { ENDPOINTS } from "@/api/application-api";
import { useInvalidateFolderEntitiesForEntity } from "@/queries/folder-entities/useInvalidateFolderEntitiesForEntity";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { displayErrorToast } from "@/utils/toast";

export const useUpdateInputVariableSet = (userToken: string) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();
  const invalidateFolderEntities = useInvalidateFolderEntitiesForEntity();

  return useMutation<
    Response<InputVariableSet, "input_variable_set", string>,
    Error,
    { id: number; data: UpdateInputVariableSetRequest }
  >({
    mutationFn: async ({ id, data }: { id: number; data: UpdateInputVariableSetRequest }) => {
      const response = await fetch(ENDPOINTS.input_variable_sets(Number(workspaceId), id), {
        method: "PUT",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(data),
      });

      return handleResponse(response);
    },
    onSuccess: (args, { id }) => {
      const numericWorkspaceId = Number(workspaceId);
      queryClient.invalidateQueries({
        queryKey: ["input-variable-sets", numericWorkspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["input-variable-sets", numericWorkspaceId, id],
      });
      invalidateFolderEntities(args?.input_variable_set?.folder_id ?? null);
      queryClient.invalidateQueries({
        queryKey: ["folders", args?.input_variable_set?.folder_id],
      });
    },
    onError: () => {
      displayErrorToast("Failed to update input variable set");
    },
  });
};
